/**
 * Admin view types.
 * Shared between relay and dashboard.
 */

import type { TradingRoom } from './rooms.js';

// ── Portfolio / Room summaries ────────────────────────────────────────────────

export interface RoomSummary {
  room: TradingRoom;
  positionCount: number;
  orderCount: number;
  totalPnl: number;
  defensePosture: string;
  activeMembers: number;
  lastActivity: string;
}

export interface OrgPortfolioSummary {
  rooms: RoomSummary[];
  totalPnl: number;
  totalExposure: number;
  activeTraders: number;
  totalPositions: number;
  totalOrders: number;
}

// ── Trader stats ──────────────────────────────────────────────────────────────

export interface TraderStats {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  tradeCount24h: number;
  pnl24h: number;
  activeRooms: number;
  lastTradeAt: string | null;
}

// ── Trade log ─────────────────────────────────────────────────────────────────

export interface TradeLogEntry {
  id: string;
  roomId: string;
  roomName: string;
  userId: string;
  displayName: string;
  credentialSetId: string | null;
  action: 'order_placed' | 'order_cancelled' | 'order_modified';
  ticker: string | null;
  side: 'yes' | 'no' | null;
  quantity: number | null;
  priceCents: number | null;
  orderId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface TradeLogQuery {
  roomId?: string;
  userId?: string;
  ticker?: string;
  limit?: number;
  offset?: number;
}

// ── Credential sets (admin-visible shape) ─────────────────────────────────────

export interface CredentialSet {
  id: string;
  orgId: string;
  label: string;
  accessKey: string;
  environment: 'prod' | 'demo';
  addedBy: string | null;
  addedByName: string | null;
  createdAt: string;
}

export interface CreateCredentialSetRequest {
  label: string;
  accessKey: string;
  privateKey: string; // plaintext — encrypted server-side
  environment: 'prod' | 'demo';
}

// ── User management ───────────────────────────────────────────────────────────

export interface UserListEntry {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: 'admin' | 'manager' | 'trader';
  maxContracts: number | null;
  maxPositions: number | null;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface InviteUserRequest {
  email: string;
  role?: 'admin' | 'manager' | 'trader';
}

export interface UpdateUserRoleRequest {
  role: 'admin' | 'manager' | 'trader';
}

// ── Trader limits ─────────────────────────────────────────────────────────────

export interface UpdateTraderLimitsRequest {
  maxContracts?: number | null; // null = clear (unlimited)
  maxPositions?: number | null;
  traderRules?: TraderRules | null; // null clears entire rules object
}

/** Sport taxonomy for trader-rules sport allowlist. Mirrors the parser-side
 *  classifier in the relay. Adding a sport requires updating the relay's
 *  classifier + this enum + the Manager UI's sport-list source. */
export type Sport =
  | 'MLB'
  | 'NBA'
  | 'WNBA'
  | 'NFL'
  | 'NHL'
  | 'NCAA'
  | 'Tennis'
  | 'Soccer'
  | 'CFB'
  | 'UFC'
  | 'Boxing'
  | 'Cricket'
  | 'Esports';

export const KNOWN_SPORTS: readonly Sport[] = [
  'MLB',
  'NBA',
  'WNBA',
  'NFL',
  'NHL',
  'NCAA',
  'Tennis',
  'Soccer',
  'CFB',
  'UFC',
  'Boxing',
  'Cricket',
  'Esports',
];

/** Per-trader rule object stored as JSONB on users.trader_rules. NULL on
 *  the column means "no rules configured" (legacy / fully unrestricted). */
export interface TraderRules {
  /** Whitelist of sport codes. null = no sport restriction. Empty array is
   *  invalid and the relay refuses to persist it. */
  allowedSports: Sport[] | null;
  /** Independent AND-gate: non-sport markets (politics, crypto, etc.)
   *  blocked when false. */
  nonSportsEnabled: boolean;
  /** Allow new Polymarket buys. Sells/cancels always pass regardless of
   *  this flag — it's a "don't open new Polymarket exposure" gate, not
   *  a hard venue halt. */
  polymarketEnabled: boolean;
  /** Oscar overtake/blocker sequence access. FAIL-CLOSED, unlike the other
   *  fields: null rules or a missing key = disabled (opt-in capability, not
   *  a restriction). Relay twin: relay/src/db/schema.ts TraderRules. */
  oscarEnabled?: boolean;
  /** Armed one-click placement access. FAIL-CLOSED: absent/null rules means
   *  disabled. Relay twin: relay/src/db/schema.ts TraderRules. */
  oneClickEnabled?: boolean;
  /** Live Trading V2 surface access (D-020): derivative spread/total one-click,
   *  two-sided ladders, A/D hotkeys, multi-line view. FAIL-CLOSED like
   *  oneClickEnabled — absent/null rules means disabled, which is why the
   *  manager UI renders a missing key as OFF rather than as unknown.
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  lt2Enabled?: boolean;
  /** Explicit per-trader ceiling on a single `lt-` tagged (one-click/hotkey)
   *  order's contract count. ABSENT IS NOT UNLIMITED and not zero: the relay
   *  falls back to its own 500 default for V1 one-click, and refuses an LT2
   *  order outright (`LT2_SIZE_LIMIT_REQUIRED`). Whole contracts only, within
   *  [ONE_CLICK_CEILING_MIN, ONE_CLICK_CEILING_MAX].
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  oneClickMaxContracts?: number;
}

/** Bounds the relay enforces on `TraderRules.oneClickMaxContracts`
 *  (relay/src/routes/manager.ts parseTraderRulesField, against
 *  relay/src/kalshi/oneClickPolicy.ts ONE_CLICK_HARD_MAX_CONTRACTS). A value
 *  outside this range is rejected with a 400, so the manager UI refuses it
 *  before the round-trip. Declared here, beside the field it bounds, rather
 *  than imported from stores/oneClickStore — that module is the trading
 *  store's hard client clamp and pulls the whole order-submission graph with
 *  it, which has no business in the manager plane. */
export const ONE_CLICK_CEILING_MIN = 1;
export const ONE_CLICK_CEILING_MAX = 10_000;

export type LimitRejectionCode =
  | 'LIMIT_MAX_CONTRACTS'
  | 'LIMIT_MAX_POSITIONS'
  | 'LIMIT_SPORT_NOT_ALLOWED'
  | 'LIMIT_NON_SPORT_DISABLED'
  | 'LIMIT_POLYMARKET_DISABLED'
  | 'LIMIT_MARKET_SPORT_UNKNOWN';

export interface LimitRejectionBreach {
  rule: LimitRejectionCode;
  cap: number;
  current: number;
  requested: number;
}

export interface LimitRejectionResponse {
  error: string;
  code: LimitRejectionCode;
  breach: LimitRejectionBreach;
}

/** Server defaults applied on POST /api/manager/traders. */
export const TRADER_LIMIT_DEFAULTS = {
  maxContracts: 10_000,
  maxPositions: 20,
} as const;

/** Default trader_rules object applied at new-trader insert time.
 *  Sport-unrestricted, non-sports blocked (matches "not engaged in non-sport
 *  Kalshi markets" stance), Polymarket allowed. Confirmed default 2026-05-23. */
export const TRADER_RULES_DEFAULTS: TraderRules = {
  allowedSports: null,
  nonSportsEnabled: false,
  polymarketEnabled: true,
};

export interface TraderHeadroom {
  maxContracts: number | null;
  maxPositions: number | null;
  openContracts: number;
  openPositions: number;
  intentContracts: number;
  /** Quick summary of which rules are active so the LimitsWidget can show
   *  a compact suffix without re-pulling the full traderRules object. */
  rulesActive?: {
    sportRestricted: boolean;
    nonSportsBlocked: boolean;
    polymarketBlocked: boolean;
  };
}
